import React, { useEffect, useMemo, useState, KeyboardEvent, ChangeEvent } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Check, X, Shield, Eye, Edit, Trash2 } from 'lucide-react'
import Permissions from "./Permissions";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Role } from "types";
import { Row } from "@tanstack/react-table";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";

import { getAuthCookie } from "../../utils/authCookies";

function fuzzyTextFilter<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
) {
    const raw = row.getValue(columnId);
    const value = String(raw).toLowerCase();
    return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Role>();

interface RolesPermissionsProps {
    module?: 'signage' | 'brokerage';
}

export default function RolesPermissions({ module = 'signage' }: RolesPermissionsProps) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const token = getAuthCookie("sessionToken");
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);

    // State for permissions view
    const [showPermissions, setShowPermissions] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);


    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/roles/datatable`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        page: pagination.pageIndex + 1,
                        limit: pagination.pageSize,
                        search: globalFilter,
                        filters: {},
                        sort: {
                            field: "created_at",
                            order: "desc"
                        },
                        fields: ["name", "description", "is_default"],
                    }),
                }
            );
            const result = await response.json();
            if (Array.isArray(result.data)) {
                setRoles(result?.data || []);
                setRowCount(result?.total || 0);
            } else {
                setRoles([]);
                setRowCount(0);
            }
        } catch (err) {
            showError("Failed to fetch roles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [token, globalFilter, pagination.pageIndex, pagination.pageSize]);


    const handleBackToRoles = () => {
        setShowPermissions(false);
        setSelectedRole(null);
    };

    const handleDeleteClick = (role: Role) => {
        setDeleteTarget({ id: role._id, name: role.name || "Role" });
    };

    const handleDeleteCancel = () => {
        setDeleteTarget(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        await handleDelete(deleteTarget.id);
        setDeleteTarget(null);
    };

    // Handle delete role
    const handleDelete = async (roleId: string) => {
        setDeleting(roleId);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/roles/${roleId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const result = await response.json();
            if (!response.ok || result?.error) {
                const message = result?.error || result?.message || "Failed to delete role";
                showError(message);
                return;
            }

            showSuccess("Role deleted successfully!");
            
            // Update local state instead of refreshing
            setRoles((prev) => prev.filter((role) => role._id !== roleId));
            setRowCount((prev) => prev - 1);
        } catch (err: any) {
            const message = err?.message || "Something went wrong";
            showError(message);
        } finally {
            setDeleting(null);
        }
    };


    // Calculate pagination info
    const totalPages = Math.ceil(rowCount / pagination.pageSize);
    const startItem = pagination.pageIndex * pagination.pageSize + 1;
    const endItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount);

    // Define columns for the table
    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: "serial",
                header: () => (
                    <div className="th-text text-center S-No">S.No.</div>
                ),
                cell: (info) => (
                    <div className="text-sm text-center S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
                enableGlobalFilter: false,
            }),
            columnHelper.accessor(
                (row: Role) => `${row?.name}`,
                {
                    id: "name",
                    header: () => (
                        <div className="th-text text-center">Role Name</div>
                    ),
                    cell: (info) => (
                        <div className="text-sm text-center">{info.getValue()}</div>
                    ),
                    filterFn: fuzzyTextFilter,
                    enableGlobalFilter: true,
                }
            ),
            // columnHelper.accessor("description", {
            //     header: () => (
            //         <div className="th-text text-center">Description</div>
            //     ),
            //     cell: (info) => (
            //         <div className="text-sm text-center">
            //             {info.getValue() || 'No description'}
            //         </div>
            //     ),
            //     enableGlobalFilter: true,
            // }),
            // columnHelper.accessor("is_default", {
            //     header: () => (
            //         <div className="th-text text-center">Default</div>
            //     ),
            //     cell: (info) => {
            //         const isDefault = info.getValue();
            //         return (
            //             <div className="text-sm text-center">
            //                 {isDefault ? (
            //                     <span className="badge bg-primary">Default</span>
            //                 ) : (
            //                     <span className="text-muted">-</span>
            //                 )}
            //             </div>
            //         );
            //     },
            //     enableGlobalFilter: true,
            // }),
            columnHelper.display({
                id: "actions",
                header: () => (
                    <div className="th-text text-center">Actions</div>
                ),
                cell: (info) => {
                    const roleInfo = info.row.original;
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <ViewIconButton
                                to={`/${module === 'brokerage' ? 'realtor' : module}/roles/${roleInfo?._id}/permissions/edit`}
                                title="View"
                                ariaLabel="View"
                            />
                            <EditIconButton
                                to={`/${module === 'brokerage' ? 'realtor' : module}/roles/${roleInfo?._id}/permissions/edit`}
                                title="Edit"
                                ariaLabel="Edit"
                            />
                            <DeleteIconButton
                                title="Delete"
                                ariaLabel="Delete"
                                onClick={() => handleDeleteClick(roleInfo)}
                                disabled={deleting === roleInfo?._id}
                            />
                        </div>
                    );
                },
            }),
        ];
    }, [deleting, module, pagination]);

    const table = useReactTable({
        data: roles,
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: {
            pagination,
            globalFilter,
        },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
    });


    const formik = useFormik({
        initialValues: {
            roleName: "",
        },
        validationSchema: Yup.object({
            // roleName: Yup.string().required("role Name is required"),
        }),
        onSubmit: async (values) => {
            if (!values.roleName.trim()) return;
            setSubmitting(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/roles`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        name: values.roleName.trim(),
                        permission_ids: [],
                        description: "",
                        is_default: false,
                    }),
                });

                const result = await response.json();
                if (!response.ok || result?.error) {
                    const message = result?.error || result?.message || "Failed to create role";
                    // replace with your toast helper if different
                    if (typeof showError === "function") showError(message);
                    return;
                }

                showSuccess("Role created successfully!");
                setFiltersOpen(false);
                formik.resetForm(); // Clear the form
                fetchRoles(); // Refresh the table without page reload
            } catch (err: any) {
                const message = err?.message || "Something went wrong";
                if (typeof showError === "function") showError(message);
                else alert(message);
            } finally {
                setSubmitting(false);
            }
        },
    });
    return (
        <>
            <div className="container">
                <div>
                    <h2>Roles & Permissions</h2>
                    <p className="text-muted fs-13">Manage user access and permissions for different modules and features</p>
                </div>
                <div className="po-header mb-4 shadow-border-datatables">
                    {/* TOP ROW */}
                    <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                        <div className="po-title-wrap me-2 d-flex align-items-center gap-3">
                            <div className="addrole p-2 rounded-5">
                                <Shield size={20} className="" />
                            </div>
                            <h4 className="fs-24 fw-normal mb-1 po-title">Role Management</h4>
                        </div>

                        {/* tools block (search + buttons) */}
                        <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                            {/* Filters toggle */}
                            {
                                !filtersOpen &&
                                <button
                                    type="button"
                                    className={`btn btn-primary table-addbutton ${filtersOpen ? "active" : ""}`}
                                    onClick={() => setFiltersOpen(v => !v)}
                                    aria-expanded={filtersOpen}
                                >
                                    + Add Role
                                </button>
                            }
                        </div>
                    </div>

                    <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
                        <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
                            <form
                                onSubmit={formik.handleSubmit}
                                className="row g-2 g-md-3 align-items-end ms-md-auto w-100 w-md-auto"
                            >
                                <div className="align-items-center col-12 col-md-6 d-flex gap-3">
                                    <input type="text" name="roleName" className="form-control br-8 rs__control" placeholder="Enter Role Name" value={formik.values.roleName} onChange={formik.handleChange} style={{ height: 45 }} />
                                    <button
                                        type="submit"
                                        className="btn btn-success br-8 mt-2 add"
                                        disabled={submitting || !formik.values.roleName.trim()}
                                        title="Create Role"
                                    >
                                        {submitting ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <Check size={20} />
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn mt-2 br-8 close"
                                        onClick={() => setFiltersOpen(false)}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                    {
                        <style>
                            {`
                        .addrole{
                        background: #e8f0ff !important;
                        color: #155dfc !important;
                        line-height: 1;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        }
                        .close {
                            background-color: #ffffff;
                            border: 0.5px solid #d1d5dc;
                        }
                        .close:hover {
                            background-color: #f3f4f6;
                            border: 0.5px solid #d1d5dc;
                        }
                        .add {
                        background-color: #00a63e !important;
                        color: white !important;
                        }
                        /* ✅ correct pseudo-class + override Bootstrap */
                        .add:disabled,
                        .add:disabled:hover,
                        .add:disabled:focus {
                          opacity: 0.5 !important;
                          color: white;
                        }
                `}
                        </style>
                    }
                </div>

            
                    <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                        {/* Header */}
                        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                            <h5 className="mb-0">Roles</h5>
                        </div>
                        <hr className="mb-2" style={{ borderTop: "none" }} />

                        {/* DESKTOP (≥ 1022px) */}
                        <div className="rdt-desktop">
                            <div
                                className="production-scroll"
                                style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                            >
                                <table className="table production-table align-middle mb-0 table-1">
                                    <DesktopTableParts table={table} />
                                </table>
                            </div>
                        </div>

                        {/* MOBILE (≤ 1021px) */}
                        <div className="rdt-mobile">
                            <MobileCardsOnly
                                table={table}
                                idColId="serial"
                                titleColId="name"
                                actionColId="actions"
                            />
                        </div>
                    </div>

                {/* Pagination Controls */}
                {!showPermissions && table.getRowModel().rows.length === 0 ? (
                    <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                        No records found
                    </div>
                ) : !showPermissions ? (
                    <div className="mt-4 mb-4">
                        <TablePaginationControls table={table} />
                    </div>
                ) : null}
                <DeleteConfirmModal
                    open={!!deleteTarget}
                    dynamicName={deleteTarget?.name || "Role"}
                    onCancel={handleDeleteCancel}
                    onDelete={handleDeleteConfirm}
                />
            </div >
        </>
    );
}